// `reports/page/*` — the full-page Reports dialog at `/chrome-extension/reports`.
import { createElement, useEffect, useState, type ReactElement } from 'react'
import {
  csvFilenameSuffix,
  exportUsageReportToCsv,
  generateEnrichmentReport,
  generateUsageReport,
  getProductName,
  type EnrichmentReportResponse,
  type UsageReportRequest,
  type UsageReportResponse,
} from '@woo-ext/api'
import { asset } from '../assetBase.ts'
import { EnrichmentReport } from './EnrichmentReport.tsx'
import { UsageReport } from './UsageReport.tsx'
import { saveFile } from './saveFile.ts'

export interface ReportsPageProps {
  /** `window.careero.atsName`, passed down for the exported-candidates tooltip's Bullhorn clause. */
  atsName?: string
  /** `getAtsLabel($window)` — the ATS the same tooltip NAMES (5cfd3ef899). */
  atsLabel?: string
}

export function ReportsPage({ atsName, atsLabel }: ReportsPageProps): ReactElement {
  const [productName, setProductName] = useState('')
  const [enrichment, setEnrichment] = useState<EnrichmentReportResponse | undefined>(undefined)
  const [enrichmentLoading, setEnrichmentLoading] = useState(false)
  const [usage, setUsage] = useState<UsageReportResponse | undefined>(undefined)
  const [usageLoading, setUsageLoading] = useState(false)
  const [lastRequest, setLastRequest] = useState<UsageReportRequest | undefined>(undefined)

  useEffect(() => {
    let live = true
    void getProductName()
      .then((res) => {
        if (live) setProductName(res.productName)
      })
      .catch(() => {
        // Upstream has no handler. The heading simply renders without the name.
      })

    setEnrichmentLoading(true)
    void generateEnrichmentReport()
      .then((report) => {
        if (live) setEnrichment(report)
      })
      .catch(() => {
        // `// TODO avif: show error?` — upstream swallows it, and the section stays empty.
      })
      .finally(() => {
        if (live) setEnrichmentLoading(false)
      })

    return () => {
      live = false
    }
  }, [])

  // ⚠️ THIS CALL TAKES SIXTY SECONDS against a real tenant and that is not a hang — it builds
  // the report over every candidate. It is fired once on mount, before the user does
  // anything, so the screen is genuinely incomplete for a minute. Anything timing this page
  // (a capture, a smoke test) needs a budget to match.

  const onSubmit = (request: UsageReportRequest): void => {
    setLastRequest(request)
    // Upstream `delete`s the previous report before fetching, so the table is replaced by the
    // spinner rather than showing the old numbers under a new filter.
    setUsage(undefined)
    setUsageLoading(true)
    void generateUsageReport(request)
      .then((report) => {
        setUsage(report)
      })
      .catch(() => {
        // `// TODO avif: show error?` again.
      })
      .finally(() => {
        setUsageLoading(false)
      })
  }

  const onExportCsv = (): void => {
    if (lastRequest === undefined) return
    void exportUsageReportToCsv(lastRequest, csvFilenameSuffix(lastRequest))
      .then((file) => {
        saveFile(file)
      })
      .catch(() => {
        // The 200-carrying-JSON case rejects with the server's own message; upstream shows it
        // through `$rootScope.showAlert`, which this repo has no equivalent of yet.
      })
  }

  /**
   * ⚠️ THE HOST ELEMENT CARRIES `z-index: 1` AND THE FLEX COLUMN, and the class of the same
   * name carries different declarations. `chrome-extension-reports-page { z-index: 1;
   * position: relative; display: flex; flex-direction: column; flex: 1 }` is a TYPE selector
   * — a div with that class matches it never.
   */
  return createElement(
    'chrome-extension-reports-page',
    null,
    <div className="chrome-extension-reports-page">
      <div className="header">
        {/* `::ctrl.productName` is a one-time binding, so it renders empty until the call
            lands and never re-renders after. The whitelabel's name, not "Woo". */}
        <h1>
          {productName} <span>Woo report</span>
        </h1>
        {/* `cdn-src` on the tenant; `asset()` is the extension-side half of that (assetBase). */}
        <img className="logo" src={asset('/static/assets/img/woo/logo.svg')} alt="" />
      </div>
      <EnrichmentReport report={enrichment} loading={enrichmentLoading} />
      <UsageReport
        report={usage}
        loading={usageLoading}
        onSubmit={onSubmit}
        onExportCsv={onExportCsv}
        {...(atsName === undefined ? {} : { atsName })}
        {...(atsLabel === undefined ? {} : { atsLabel })}
      />
    </div>,
  )
}
